import { ArrowDownRight, ArrowUpRight, Info, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  label: string;
  value: string;
  delta?: { label: string; direction: 'up' | 'down' | 'flat' };
  /** "good" significa que valores em alta são positivos (ex.: receita).
   *  "bad" inverte (ex.: despesas, inadimplência). */
  trend?: 'good' | 'bad';
  hint?: string;
  infoTooltip?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export function StatCard({ label, value, delta, trend = 'good', hint, infoTooltip, icon: Icon }: Props) {
  const dir = delta?.direction ?? 'flat';
  const isPositive =
    dir === 'flat' ? null : trend === 'good' ? dir === 'up' : dir === 'down';

  return (
    <div className="card-pad">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1">
          <div className="stat-label">{label}</div>
          {infoTooltip && (
            <div className="relative group/tooltip">
              <Info className="w-3.5 h-3.5 text-ink-muted cursor-help shrink-0" />
              <div className={cn(
                'pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-52',
                'rounded-xl bg-ink px-3 py-2 text-[11px] text-surface leading-snug shadow-lg z-50',
                'opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-normal text-center'
              )}>
                {infoTooltip}
              </div>
            </div>
          )}
        </div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
      <div className="mt-2 stat-value">{value}</div>
      <div className="mt-1 flex items-center gap-2">
        {delta && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-xs font-semibold',
              isPositive === true && 'text-positive',
              isPositive === false && 'text-negative',
              isPositive === null && 'text-ink-muted'
            )}
          >
            {dir === 'up' && <ArrowUpRight className="w-3 h-3" />}
            {dir === 'down' && <ArrowDownRight className="w-3 h-3" />}
            {dir === 'flat' && <Minus className="w-3 h-3" />}
            {delta.label}
          </span>
        )}
        {hint && <span className="text-xs text-ink-muted">{hint}</span>}
      </div>
    </div>
  );
}
